import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, computed } from '@angular/core';
import { Params, Router } from '@angular/router';
import {FileSharingDialogComponent} from '../dialogs/file-sharing-dialog/file-sharing-dialog.component';
import {SharedFile} from '../documents/shared-file';
import {SharedFileSection} from '../documents/shared-file-section';
import {SharedFileType} from '../documents/shared-file-type';
import {PersonLogin} from '../person/person-login';
import {DocumentService} from '../services/document-service';
import {PersonLoginService} from '../services/person-login-service';
import {StringUtils} from '../utils/string-utils';
import {MatDialog} from '@angular/material/dialog';
import {EncompassTreeNode} from '../model/encompass-tree-node';
import {FileUtils} from "../utils/file-utils";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {DocumentationTreeComponent} from './documentation-tree/documentation-tree.component';

import {EncompassTreeModel} from "../model/encompass-tree-model";
import {BrandID} from "../enums/brand-id";
import {firstValueFrom} from "rxjs";

@Component({
    selector: 'marketing',
    templateUrl: './marketing.component.html',
    styleUrls: ['./marketing.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DocumentationTreeComponent, MatProgressSpinner]
})
export class MarketingComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly documentService = inject(DocumentService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);


  private personLogin!: PersonLogin;
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  readonly documents = input<string>(undefined!);
  readonly software = input<string>(undefined!);
  readonly revit = input<string>(undefined!);
  readonly presentations = input<string>(undefined!);
  readonly category = input<string>(undefined!);
  readonly openDocumentation = computed(() => !!this.documents());
  readonly openSoftware = computed(() => !!this.software());
  readonly openRevit = computed(() => !!this.revit());
  readonly openPresentations = computed(() => !!this.presentations());
  readonly selectedCategory = computed(() => this.category());
  readonly documentationNodes = signal<EncompassTreeNode[]>([]);
  readonly softwareNodes = signal<EncompassTreeNode[]>([]);
  readonly revitFileNodes = signal<EncompassTreeNode[]>([]);
  readonly presentationFileNodes = signal<EncompassTreeNode[]>([]);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      if (!this.personLogin) {
        this.router.navigate(['main']);
      } else {
        this.clearStatus();
        this.getMarketingDocuments();
      }
    }
  }

  public getMarketingDocuments() {
    this.isBusy.set(true);
    this.loadDocumentLists().then(() => this.isBusy.set(false), error => this.handleError(error));
  }

  private async loadDocumentLists(): Promise<void> {
    this.documentationNodes.set(this.sortTree(await this.getMarketingDocs()));
    this.softwareNodes.set(this.sortTree(await firstValueFrom<EncompassTreeModel>(this.documentService.getMarketingSoftware())));
    this.revitFileNodes.set(this.sortTree(await firstValueFrom<EncompassTreeModel>(this.documentService.getRevitFiles())));
    const presentationModel: EncompassTreeModel = await firstValueFrom<EncompassTreeModel>(this.documentService.getPresentationFiles());
    if (presentationModel) {
      const placeholderFile: EncompassTreeNode[] = presentationModel.nodes[0].children.filter(node => node.displayName.toUpperCase() === 'THUMBS.DB.NONE');
      if (placeholderFile.length === 1) {
        presentationModel.nodes[0].children.splice(presentationModel.nodes[0].children.indexOf(placeholderFile[0]), 1);
      }
      this.presentationFileNodes.set(this.sortTree(presentationModel));
    }
  }

  private sortTree(treeModel: EncompassTreeModel): EncompassTreeNode[] {
    if ((treeModel?.nodes?.length || 0) > 0) {
      return this.documentService.sortNodes(treeModel.nodes);
    }
    return [];
  }

  private async getMarketingDocs(): Promise<EncompassTreeModel> {
    const brandIds: BrandID[] = this.personLoginService.getBrandIDs();
    if (brandIds.length > 0) {
      return await firstValueFrom<EncompassTreeModel>(this.documentService.getMarketingDocuments(BrandID[brandIds[0]]));
    }
    return undefined!;
  }

  public download(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.downloadMarketingDocument(+node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadSoftware(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.downloadMarketingSoftware(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadRevit(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.downloadRevitFile(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadPresentation(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentService.downloadPresentationFile(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.documentationNodes.set([]);
    this.softwareNodes.set([]);
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  shareSoftware(fileName: string): void {
    let sharedFile: SharedFile = this.buildSharedFile(SharedFileType.SOFTWARE, fileName);
    this.openFileShareDialog(sharedFile);
  }

  private openFileShareDialog(sharedFile: SharedFile): void {
    this.dialog.open(FileSharingDialogComponent, {
      width: '680px',
      height: '200px',
      data: {sharedFile: sharedFile},
    });
  }

  private buildSharedFile(type: SharedFileType, fileName: string): SharedFile {
    let sharedFile: SharedFile = new SharedFile();
    sharedFile.fileName = fileName;
    sharedFile.section = SharedFileSection.MARKETING;
    sharedFile.type = type;
    sharedFile.validDays = 3;
    return sharedFile;
  }

  shareRevit(fileName: string): void {
    let sharedFile: SharedFile = this.buildSharedFile(SharedFileType.REVIT, fileName);
    this.openFileShareDialog(sharedFile);
  }

}
